import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { renderDisplayText } from "@/lib/utils";
import { useTenantPath } from "@/hooks/useTenantPath";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";

const STEPS = [
  {
    title: "Choose your clean",
    description: "Pick the service that fits, tell us a little about your space and see your price upfront.",
  },
  {
    title: "Pick a time",
    description: "Choose a date and time that suits you and book online in a couple of minutes.",
  },
  {
    title: "Come home to spotless",
    description: "Your cleaner arrives ready to work, and you get back a space that feels brand new.",
  },
];

/** "How it works" — three steps from browsing to a finished clean. */
export function HowItWorks() {
  const tenantPath = useTenantPath();
  const demo = useDemoShowcase();

  return (
    <section className="tp-section tp-tint relative">
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="tp-eyebrow" data-reveal>{demo?.howItWorks.eyebrow ?? "How it works"}</p>
            <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
              {renderDisplayText(demo?.howItWorks.title ?? "Three simple steps to a spotless space")}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-1" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
            <p className="tp-lead">{demo?.howItWorks.lead ?? "Booking a professional clean should feel effortless. Here is exactly what happens."}</p>
          </div>
        </div>

        <ol className="mt-12 grid gap-5 md:mt-16 md:grid-cols-3 md:gap-6">
          {STEPS.map((step, index) => (
            <li key={step.title} data-reveal style={{ ["--reveal-delay" as string]: `${index * 110}ms` }}>
              <div className="tp-card flex h-full flex-col p-7 md:p-8">
                <div className="flex items-center gap-5">
                  <span className="tp-numeral text-[2.75rem] text-primary md:text-[3.25rem]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="tp-rule flex-1" aria-hidden />
                </div>
                <h3 className="tp-h3 mt-8 md:mt-10">{renderDisplayText(step.title)}</h3>
                <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex justify-center md:mt-12" data-reveal>
          <Link to={tenantPath("/booking")} className="tp-link text-sm">
            Start your booking <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
